import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import html2pdf from 'html2pdf.js';
import type { NewQuoteInfo } from '@/typings/quotes/NewQuoteInfo';
import type { Unit } from '@/typings/units/Unit';
import { formatAvailabilityDate } from '@/helpers/dateHelpers';
import { isUnitDisabled } from '@/enums/UnitStatus';
import { getUnitPositionText } from '@/enums/UnitPosition';

interface PdfGeneratorOptions {
  unit: Unit;
  quoteInfo: NewQuoteInfo;
  selectedConfigurationId?: number;
  selectedOptionIds: number[];
  locale: string;
  t: (key: string, params?: Record<string, unknown>) => string;
  formatCurrency: (value: number) => string;
  calculatedPrice?: number;
  priceBoxTitle: string;
  floorName: string;
  optionsHeading: string;
  promotion?: string;
  showCostDetails?: boolean;
  priceSubtitle?: string;
  availabilityDate?: string;
  description?: string;
  isRentalMode?: boolean;
  isFirstTimeHomebuyer?: boolean;
}

export function usePdfGenerator() {
  const isCreatingPdf = ref(false);
  const pdfCreationDelay = ref(1500);
  const pdfPreparationStatus = ref('');
  const { t } = useI18n();

  // Fonction pour nettoyer le HTML de la description et minimiser l'espace
  const cleanDescriptionHtml = (html: string | undefined): string => {
    if (!html) return '';
    
    // Créer un élément temporaire pour parser le HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    
    // Retirer tous les attributs style et class, et ajouter des styles compacts
    const allElements = tempDiv.querySelectorAll('*');
    allElements.forEach(element => {
      element.removeAttribute('style');
      element.removeAttribute('class');
      
      // Ajouter des styles compacts selon le type d'élément
      const tagName = element.tagName.toLowerCase();
      if (tagName === 'p') {
        element.setAttribute('style', 'margin: 0 0 4px 0; line-height: 1.4;');
      } else if (tagName === 'ul' || tagName === 'ol') {
        element.setAttribute('style', 'margin: 0 0 4px 0; padding-left: 20px;');
      } else if (tagName === 'li') {
        element.setAttribute('style', 'margin: 0 0 2px 0; line-height: 1.4;');
      } else if (['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(tagName)) {
        element.setAttribute('style', 'margin: 4px 0 2px 0; line-height: 1.3; font-weight: 600;');
      } else if (tagName === 'br') {
        // Garder les <br> mais sans style
      } else {
        element.setAttribute('style', 'margin: 0; line-height: 1.4;');
      }
    });
    
    return tempDiv.innerHTML;
  };

  function generatePdfHtmlContent(
    options: PdfGeneratorOptions,
    logoBase64: string,
    unitPlanSrc: string | null,
    buildingSrc: string | null,
    styleImages: string[] = []
  ): string {
    const fontFamily = '\'Inter\', -apple-system, BlinkMacSystemFont, \'Segoe UI\', Roboto, Oxygen, Ubuntu, sans-serif';
    
    const titleElement = document.querySelector('.title-container');
    const pageTitle = titleElement ? titleElement.textContent?.trim() || 'Fiche Unité' : 'Fiche Unité';

    const selectedOptions = options.quoteInfo?.options?.filter((option: { optionId: number; quantity: number }) => {
      if (options.selectedOptionIds && options.selectedOptionIds.length > 0) {
        return options.selectedOptionIds.includes(option.optionId);
      }
      return option.quantity > 0;
    }) || [];

    // Calculer la date de disponibilité formatée
    const formattedAvailabilityDate = formatAvailabilityDate(options.unit.availabilityDate, options.locale, options.t, options.isRentalMode ? 'rental' : 'purchase', options.unit.status);
    // Calculer le texte de la position de l'unité
    const formattedPosition = (() => {
      try {
        const pos = getUnitPositionText(options.unit.position, options.t as unknown as (key: string) => string);
        return pos || options.t('common.notAvailableAbbreviated');
      } catch {
        return options.t('common.notAvailableAbbreviated');
      }
    })();

    return `
      <div style="font-family: ${fontFamily}; color: #333; line-height: 1.6; white-space: normal; word-spacing: normal; letter-spacing: normal;">
        <div style="background:#ffffff;">
          <!-- En-tête avec logo -->
          <div style="text-align: left; padding:16px 20px; background-color:#2c2c2c; page-break-inside: avoid;">
            <h1 style="color: #ffffff; margin:0; font-size: 20px; font-weight: 700; font-family: ${fontFamily}; white-space: normal; word-spacing: normal; letter-spacing: normal;">${pageTitle}</h1>
          </div>
        
          <div>
            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 12px; padding:20px 20px 12px 20px; break-after: page;">
              <!-- COLONNE GAUCHE : DÉTAILS DE L'UNITÉ -->
              <div style="text-align: center;">
                <!-- Section Prix -->
                <!-- Informations de l'unité -->
                <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 4px; border-radius: 8px; padding: 12px; margin-bottom: 12px; color: #000000; text-align: left; border: 1px solid #e9ecef;">
                  <div>
                    <h4 style="color: #000000; margin: 0; font-size: 14px; font-weight: 600; font-family: ${fontFamily}; white-space: normal; word-spacing: normal; letter-spacing: normal;">${options.t('browsingApp.regions.label')}</h4>
                    <p style="margin: 0; font-size: 12px;">${options.quoteInfo?.unit?.localizedRegionName || options.t('common.notAvailable')}</p>
                  </div>
                  <div>
                    <h4 style="color: #000000; margin: 0; font-size: 14px; font-weight: 600; font-family: ${fontFamily}; white-space: normal; word-spacing: normal; letter-spacing: normal;">${options.t('browsingApp.projects.label')}</h4>
                    <p style="margin: 0; font-size: 12px;">${options.quoteInfo?.unit?.localizedProjectName || options.t('common.notAvailable')}</p>
                  </div>
                  <div>
                    <h4 style="color: #000000; margin: 0; font-size: 14px; font-weight: 600; font-family: ${fontFamily}; white-space: normal; word-spacing: normal; letter-spacing: normal;">${options.t('browsingApp.buildings.buildingNumber')}</h4>
                    <p style="margin: 0; font-size: 12px;">${options.quoteInfo?.unit?.buildingNo || options.t('common.notAvailable')}</p>
                  </div>
                  <div>
                    <h4 style="color: #000000; margin: 0; font-size: 14px; font-weight: 600; font-family: ${fontFamily}; white-space: normal; word-spacing: normal; letter-spacing: normal;">${options.t('browsingApp.buildings.floor')}</h4>
                    <p style="margin: 0; font-size: 12px;">${options.floorName}</p>
                  </div>
                  <div>
                    <h4 style="color: #000000; margin: 0; font-size: 14px; font-weight: 600; font-family: ${fontFamily}; white-space: normal; word-spacing: normal; letter-spacing: normal;">${options.t('browsingApp.units.unitNumber')}</h4>
                    <p style="margin: 0; font-size: 12px;">${options.quoteInfo?.unit?.unitNumber || options.t('common.notAvailable')}</p>
                  </div>
                  <div>
                    <h4 style="color: #000000; margin: 0; font-size: 14px; font-weight: 600; font-family: ${fontFamily}; white-space: normal; word-spacing: normal; letter-spacing: normal;">${options.t('unitSummary.roomCount')}</h4>
                    <p style="margin: 0; font-size: 12px;">${(() => {
                      if (options.selectedConfigurationId && options.unit.unitUnitConfigurations) {
                        const selectedConfig = options.unit.unitUnitConfigurations.find(
                          c => c.unitConfigurationId === options.selectedConfigurationId
                        );
                        if (selectedConfig) {
                          return options.locale.toLowerCase().startsWith('en')
                            ? (selectedConfig.unitConfigurationNameEn || selectedConfig.unitConfigurationName || 'n.d.')
                            : (selectedConfig.unitConfigurationName || selectedConfig.unitConfigurationNameEn || 'n.d.');
                        }
                      }
                      return 'n.d.';
                    })()}</p>
                  </div>
                  ${options.unit.livingArea ? `
                  <div>
                    <h4 style="color: #000000; margin: 0; font-size: 14px; font-weight: 600; font-family: ${fontFamily}; white-space: normal; word-spacing: normal; letter-spacing: normal;">${options.t('browsingApp.units.livingArea')}</h4>
                    <p style="margin: 0; font-size: 12px;">${new Intl.NumberFormat(options.locale.toLowerCase()).format(options.unit.livingArea)} pi²</p>
                  </div>
                  ` : ''}
                  ${options.unit.hasChargingStation ? `
                  <div>
                    <h4 style="color: #000000; margin: 0; font-size: 14px; font-weight: 600; font-family: ${fontFamily}; white-space: normal; word-spacing: normal; letter-spacing: normal;">${options.t('unitSummary.chargingStation')}</h4>
                    <p style="margin: 0; font-size: 12px;">${options.locale.toLowerCase().startsWith('en') ? 'Yes' : 'Oui'}</p>
                  </div>
                  ` : ''}
                  <div>
                    <h4 style="color: #000000; margin: 0; font-size: 14px; font-weight: 600; font-family: ${fontFamily}; white-space: normal; word-spacing: normal; letter-spacing: normal;">${options.isRentalMode ? options.t('unitSummary.moveInDate') : options.t('unitSummary.availabilityDate')}</h4>
                    <p style="margin: 0; font-size: 12px;">${formattedAvailabilityDate}</p>
                  </div>
                  <div>
                    <h4 style="color: #000000; margin: 0; font-size: 14px; font-weight: 600; font-family: ${fontFamily}; white-space: normal; word-spacing: normal; letter-spacing: normal;">${options.t('browsingApp.units.position')}</h4>
                    <p style="margin: 0; font-size: 12px;">${formattedPosition}</p>
                  </div>
                </div>
                
                <!-- Options préconfigurées -->
                ${selectedOptions && selectedOptions.length > 0 ? `
                <div style="border-radius: 8px; padding: 12px; margin-bottom: 12px; color: #000000; text-align: left; border: 1px solid #e9ecef;">
                  <h4 style="color: #000000; margin: 0; font-size: 14px; font-weight: 600; font-family: ${fontFamily}; white-space: normal; word-spacing: normal;">${options.optionsHeading}</h4>
                  <div style="display: flex; flex-direction: column; gap: 0px;">
                    ${selectedOptions.map((option: { localizedOptionName?: string; optionName?: string; price?: number }) => `
                      <div style="display: flex; justify-content: space-between; align-items: center;">
                        <span style="margin: 0; font-size: 12px; font-weight: 400;">${option.localizedOptionName || option.optionName || 'Option'}</span>
                        ${!isUnitDisabled(options.unit.status) && !options.unit.hasDefaultOptions && option.price ? `<span style="font-size: 12px; font-weight: 400; color: #000000;">${new Intl.NumberFormat('fr-CA', { style: 'currency', currency: 'CAD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(option.price)}</span>` : (!isUnitDisabled(options.unit.status) ? `<span style="font-size: 12px; color: #6c757d;">${options.t('common.included')}</span>` : '')}
                      </div>
                    `).join('')}
                  </div>
                </div>
                ` : ''}
                <!-- Promotion -->
                ${options.promotion ? `
                  <div style="border-radius: 8px; padding: 8px 12px; color: #000000; text-align: left; border: 1px solid #e9ecef; margin-bottom:12px;">
                    <h4 style="color: #000000; margin: 0 0 4px 0; font-size: 14px; font-weight: 600; font-family: ${fontFamily}; white-space: normal; word-spacing: normal; letter-spacing: normal;">
                      ${options.t('unitSummary.promotion')}
                    </h4>
                    <div style="margin: 0; font-size: 12px; color: #000000; line-height: 1.4;">${options.promotion}</div>
                  </div>
                ` : ''}
                ${unitPlanSrc ? `
                  <div style="text-align: center; background: white; border-radius: 8px; padding: 8px 8px; border: 1px solid #e9ecef;">
                    <h4 style="color: #000000; margin: 0 0 4px 0; font-size: 14px; font-weight: 600; font-family: ${fontFamily}; white-space: normal; word-spacing: normal; letter-spacing: normal;">${options.t('unitSummary.unitPlan')}</h4>
                    <img src="${unitPlanSrc}" alt="${options.t('unitSummary.unitPlan')}" style="width: 100%; height: auto; max-height:500px; border-radius: 4px; display:block; margin:0 auto;" />
                  </div>
                ` : ''}
              </div>

              <!-- COLONNE DROITE : DESCRIPTION -->
              <div style="display: flex; flex-direction: column; gap: 12px;">
                ${!isUnitDisabled(options.unit.status) ? `
                <div style="border-radius: 8px; padding: 8px 12px; color: #000000; text-align: center; border: 1px solid #e9ecef;">
                  ${!options.isRentalMode ? `<h4 style="color: #000000; margin: 0 0 4px 0; font-size: 14px; font-weight: 600; font-family: ${fontFamily}; white-space: normal; word-spacing: normal; letter-spacing: normal;">${options.priceBoxTitle}</h4>` : ''}
                    <div style="font-size: 18px; font-weight: 700; margin: 4px 0; line-height:1.0">
                      ${options.calculatedPrice !== undefined
                      ? (options.calculatedPrice === 0 ? options.t('common.notAvailable') : options.formatCurrency(options.calculatedPrice))
                      : (options.quoteInfo.unit.basePrice !== null ? options.formatCurrency(options.quoteInfo.unit.basePrice) : options.t('common.notAvailable'))
                      }
                    </div>
                    <div style="margin: 4px 0 0 0; font-size: 12px; line-height: 1.4;">${options.priceSubtitle || options.t('unitSummary.includingRebatesAndTaxes')}</div>
                    ${options.isFirstTimeHomebuyer ? `
                      <div style="margin: 8px 0 0 0; padding: 8px; background: #f8f9fa; border: 1px solid #e9ecef; border-radius: 4px; font-size: 12px; line-height: 1.4; text-align: left;">
                        ${options.t('unitSummary.firstTimeHomebuyer.pdfMention')}
                      </div>
                    ` : ''}
                    
                    ${options.showCostDetails !== false ? `<!-- Détails des coûts -->
                    ${generateCostDetailsTable(fontFamily, options.t)}` : ''}
                  </div>
                ` : ''}
                ${options.description ? `
                  <div>
                    <div style="border-radius: 8px; padding: 8px 12px; color: #000000; text-align: left; border: 1px solid #e9ecef;">
                      <h4 style="color: #000000; margin: 0 0 4px 0; font-size: 14px; font-weight: 600; font-family: ${fontFamily}; white-space: normal; word-spacing: normal; letter-spacing: normal;">${options.t('unitSummary.description')}</h4>
                      <div style="margin: 0; font-size: 12px; line-height: 1.4;">${cleanDescriptionHtml(options.description)}</div>
                    </div>
                  </div>
                ` : ''}
              </div>
            </div>
            
            <div style="padding: 20px 20px 12px 20px;">
              <!--Image du batiment-->
              <div>
                ${buildingSrc ? `
                  <div style="text-align: center; background: white; border-radius: 8px; padding: 8px 8px; border: 1px solid #e9ecef; margin-top:12px; width:auto;">
                    <h4 style="color: #000000; margin: 0 0 4px 0; font-size: 14px; font-weight: 600; font-family: ${fontFamily}; white-space: normal; word-spacing: normal; letter-spacing: normal;">${options.t('unitSummary.buildingImage')}</h4>
                    <img src="${buildingSrc}" alt="${options.t('unitSummary.buildingImage')}" style="max-width: 100%; max-height: 375px; height: auto; border-radius: 4px; display:block; margin:0 auto; object-fit: contain;" />
                  </div>
                ` : ''}
              </div>
              <!--Images de style-->
              <div>
                ${styleImages && styleImages.length > 0 ? `
                  <div style="background: white; border-radius: 8px; padding: 8px; border: 1px solid #e9ecef; margin-top:12px;">
                    <h4 style="color: #000000; margin: 0 0 8px 0; font-size: 14px; font-weight: 600; font-family: ${fontFamily}; text-align: center;">${options.t('unitSummary.styleImages') || 'Images de style'}</h4>
                    <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 12px;">
                      ${styleImages.map((imgSrc) => `
                        <img src="${imgSrc}" alt="Image de style" style="width: 100%; height: 190px; object-fit: cover; border-radius: 4px;" />
                      `).join('')}
                    </div>
                  </div>
                ` : ''}
              </div>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  function generateCostDetailsTable(fontFamily: string, t: (key: string) => string): string {
    const table = document.querySelector('.unitCostDetails');
    if (!table) {
      return `<div style="margin: 12px 0; padding: 8px; background: #fff3cd; color: #856404; border-radius: 4px; font-size: 12px;">
        ${t('unitCostDetails.priceNotAvailable')}
      </div>`;
    }
    
    const rows = table.querySelectorAll('tr');
    let html = `<table style="width: 100%; border-collapse: collapse; margin: 12px 0; font-size: 12px; font-family: ${fontFamily}; border: 1px solid #e9ecef;"><tbody>`;
    
    rows.forEach((row, index) => {
      const th = row.querySelector('th');
      const td = row.querySelector('td');
      
      if (th && td) {
        const isTotal = row.classList.contains('unitCostDetails-total');
        const bg = isTotal ? '#f8f9fa' : (index % 2 === 0 ? '#ffffff' : '#f9f9f9');
        const weight = isTotal ? '700' : '600';
        const padding = isTotal ? '8px' : '4px 8px';
        const borderTop = isTotal ? 'border-top: 2px solid #333;' : '';
        
        html += `<tr style="background: ${bg}; ${borderTop}">
          <td style="padding: ${padding}; font-weight: ${weight}; border-bottom: 1px solid #e9ecef; text-align:left;">${th.textContent?.trim()}</td>
          <td style="padding: ${padding}; text-align: right; font-weight: ${weight}; border-bottom: 1px solid #e9ecef;">${td.textContent?.trim()}</td>
        </tr>`;
      }
    });
    
    return html + '</tbody></table>';
  }

  function createImagePlaceholder(text: string): string {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    canvas.width = 300;
    canvas.height = 200;
    
    if (ctx) {
      ctx.fillStyle = '#f0f0f0';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = '#666';
      ctx.font = '16px Arial';
      ctx.textAlign = 'center';
      ctx.fillText(text, canvas.width / 2, canvas.height / 2 - 10);
      ctx.fillText('(Image non disponible pour PDF)', canvas.width / 2, canvas.height / 2 + 10);
      return canvas.toDataURL('image/png', 0.8);
    }
    return '';
  }

  async function loadImageThroughProxy(proxyUrl: string): Promise<string> {
    const response = await fetch(proxyUrl);
    if (!response.ok) throw new Error('Proxy failed');
    
    const blob = await response.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.readAsDataURL(blob);
    });
  }

  async function loadImageWithFetch(imageUrl: string): Promise<string> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          
          if (ctx) {
            canvas.width = img.naturalWidth || img.width;
            canvas.height = img.naturalHeight || img.height;
            ctx.drawImage(img, 0, 0);
            resolve(canvas.toDataURL('image/jpeg', 0.8));
          } else {
            reject(new Error('No canvas context'));
          }
        } catch (error) {
          reject(error);
        }
      };
      
      img.onerror = () => reject(new Error('Image load failed'));
      img.src = imageUrl;
      
      setTimeout(() => reject(new Error('Timeout')), 5000);
    });
  }

  async function loadLogoAsBase64(logoPath: string): Promise<string> {
    try {
      const logoImg = new Image();
      logoImg.src = logoPath;
      return await new Promise((resolve, _reject) => {
        logoImg.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          if (ctx) {
            canvas.width = logoImg.naturalWidth;
            canvas.height = logoImg.naturalHeight;
            ctx.drawImage(logoImg, 0, 0);
            resolve(canvas.toDataURL('image/png', 1.0));
          } else {
            resolve('');
          }
        };
        logoImg.onerror = () => resolve('');
      });
    } catch {
      return '';
    }
  }

  // Fonction pour nettoyer l'URL de l'image en retirant les paramètres de redimensionnement
  function cleanImageUrl(url: string): string {
    try {
      const urlObj = new URL(url);
      // Retirer les paramètres height, format, quality, rmode
      urlObj.searchParams.delete('height');
      urlObj.searchParams.delete('format');
      urlObj.searchParams.delete('quality');
      urlObj.searchParams.delete('rmode');
      return urlObj.toString();
    } catch {
      // Si l'URL n'est pas valide, retourner l'URL originale
      return url;
    }
  }

  async function captureCanvasAsImage(canvasElement: HTMLElement): Promise<string> {
    const svgElement = canvasElement.querySelector('svg');
    if (!svgElement) {
      throw new Error('SVG element not found');
    }

    // Trouver l'élément image dans le SVG
    const imageElement = svgElement.querySelector('image');
    if (!imageElement) {
      throw new Error('Image element not found in SVG');
    }

    const imageUrl = imageElement.getAttribute('href') || imageElement.getAttribute('xlink:href');
    if (!imageUrl) {
      throw new Error('Image URL not found');
    }

    // Obtenir les dimensions du SVG
    const width = parseInt(svgElement.getAttribute('width') || '800');
    const height = parseInt(svgElement.getAttribute('height') || '600');

    // Créer un canvas pour composer l'image finale
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    
    if (!ctx) {
      throw new Error('Canvas context not available');
    }

    // Charger l'image de fond via le proxy
    const cleanedImageUrl = cleanImageUrl(imageUrl);
    const proxyUrl = `/api/proxy-image?url=${encodeURIComponent(cleanedImageUrl)}`;
    
    let backgroundImageData: string;
    try {
      backgroundImageData = await loadImageThroughProxy(proxyUrl);
    } catch {
      // Fallback: essayer sans proxy
      backgroundImageData = await loadImageWithFetch(cleanedImageUrl);
    }
    
    // Dessiner l'image de fond
    const bgImg = new Image();
    bgImg.crossOrigin = 'anonymous';
    await new Promise((resolve, reject) => {
      bgImg.onload = resolve;
      bgImg.onerror = reject;
      bgImg.src = backgroundImageData;
    });
    
    ctx.drawImage(bgImg, 0, 0, width, height);

    // Trouver et dessiner les pastilles (foreignObject avec les points)
    const foreignObjects = svgElement.querySelectorAll('foreignObject');
    
    foreignObjects.forEach((fo) => {
      const x = parseFloat(fo.getAttribute('x') || '0');
      const y = parseFloat(fo.getAttribute('y') || '0');
      const foWidth = parseFloat(fo.getAttribute('width') || '32');
      const foHeight = parseFloat(fo.getAttribute('height') || '32');
      
      // Obtenir la couleur de la pastille depuis les styles CSS
      const pointItem = fo.closest('.renderCanvas-pointItem');
      const status = pointItem?.getAttribute('data-item-status') || 'Available';
      
      // Couleurs selon le statut
      const colorMap: Record<string, string> = {
        'Available': '#10b981',
        'Reserved': '#f59e0b',
        'Sold': '#ef4444',
        'Rented': '#ef4444',
        'Upcoming': '#eab308'
      };
      
      const color = colorMap[status] || '#6b7280';
      
      const centerX = x + foWidth / 2;
      const centerY = y + foHeight / 2;
      const radius = foWidth / 2;
      
      // Dessiner le cercle de fond noir
      ctx.fillStyle = '#000000';
      ctx.globalAlpha = 1;
      ctx.beginPath();
      ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
      ctx.fill();
      
      // Dessiner le cercle coloré principal (légèrement plus petit)
      ctx.fillStyle = color;
      ctx.globalAlpha = 0.9;
      ctx.beginPath();
      ctx.arc(centerX, centerY, radius * 0.85, 0, Math.PI * 2);
      ctx.fill();
      
      // Bordure du cercle coloré
      ctx.strokeStyle = color;
      ctx.globalAlpha = 1;
      ctx.lineWidth = 2;
      ctx.stroke();
      
      // Dessiner le point coloré au centre (même couleur que la bordure)
      ctx.fillStyle = '#000000';
      ctx.globalAlpha = 1;
      ctx.beginPath();
      ctx.arc(centerX, centerY, radius * 0.25, 0, Math.PI * 2);
      ctx.fill();
    });

    return canvas.toDataURL('image/png', 0.9);
  }

  async function loadUnitImages(): Promise<{ unitPlanBase64: string | null; buildingBase64: string | null; styleImages: string[] }> {
    // Pour Purchase: utilise les classes spécifiques
    let unitPlanImg = document.querySelector('.unit-floorPlan img') as HTMLImageElement;
    
    // Pour Rental: cherche dans unitSummary-floorPlan ou dans les figures avec figcaption
    if (!unitPlanImg) {
      unitPlanImg = document.querySelector('.unitSummary-floorPlan img') as HTMLImageElement;
    }
    
    // Chercher le plan d'unité dans les figures avec figcaption
    if (!unitPlanImg) {
      const figures = document.querySelectorAll('.unitSummary-unitAndBuildingPlan figure');
      
      figures.forEach((figure) => {
        const figcaption = figure.querySelector('figcaption');
        const img = figure.querySelector('img') as HTMLImageElement;
        
        if (figcaption && img) {
          const text = figcaption.textContent?.toLowerCase() || '';
          if (text.includes('plan') || text.includes('unité') || text.includes('unit')) {
            if (!unitPlanImg) {
              unitPlanImg = img;
            }
          }
        }
      });
    }
    
    let unitPlanBase64 = null;
    let buildingBase64 = null;
    
    if (unitPlanImg) {
      try {
        const cleanedUrl = cleanImageUrl(unitPlanImg.src);
        const proxyUrl = `/api/proxy-image?url=${encodeURIComponent(cleanedUrl)}`;
        unitPlanBase64 = await loadImageThroughProxy(proxyUrl);
      } catch {
        try {
          const cleanedUrl = cleanImageUrl(unitPlanImg.src);
          unitPlanBase64 = await loadImageWithFetch(cleanedUrl);
        } catch {
          unitPlanBase64 = createImagePlaceholder('Plan de l\'unité');
        }
      }
    }
    
    // Chercher le canvas de la façade avec la pastille - essayer plusieurs sélecteurs
    let facadeCanvas = document.querySelector('.unitSummary-buildingPlanWithCanvas') as HTMLElement;
    
    // Si pas trouvé, essayer d'autres sélecteurs
    if (!facadeCanvas) {
      facadeCanvas = document.querySelector('.unitSummary-facadeCanvas') as HTMLElement;
    }
    
    if (!facadeCanvas) {
      facadeCanvas = document.querySelector('.renderCanvas-container') as HTMLElement;
    }
    
    if (facadeCanvas) {
      try {
        buildingBase64 = await captureCanvasAsImage(facadeCanvas);
      } catch (error) {
        // En cas d'erreur, essayer de trouver l'image de fond du canvas
        const backgroundImg = facadeCanvas.querySelector('image') as SVGImageElement;
        if (backgroundImg) {
          const href = backgroundImg.getAttribute('href') || backgroundImg.getAttribute('xlink:href');
          if (href) {
            try {
              const cleanedUrl = cleanImageUrl(href);
              const proxyUrl = `/api/proxy-image?url=${encodeURIComponent(cleanedUrl)}`;
              buildingBase64 = await loadImageThroughProxy(proxyUrl);
            } catch {
              try {
                const cleanedUrl = cleanImageUrl(href);
                buildingBase64 = await loadImageWithFetch(cleanedUrl);
              } catch {
                buildingBase64 = null;
              }
            }
          }
        }
      }
    } else {
      // Fallback: chercher une image simple du bâtiment
      let buildingImg = document.querySelector('.buildingPlan img') as HTMLImageElement;
      
      if (!buildingImg) {
        const figures = document.querySelectorAll('.unitSummary-unitAndBuildingPlan figure');
        
        figures.forEach((figure) => {
          const figcaption = figure.querySelector('figcaption');
          const img = figure.querySelector('img') as HTMLImageElement;
          
          if (!figcaption && img && !buildingImg) {
            buildingImg = img;
          } else if (figcaption && img) {
            if (figcaption.id != 'unitPlanFigCaption' && !buildingImg) {
              buildingImg = img;
            }
          }
        });
      }
      
      if (buildingImg && buildingImg !== unitPlanImg) {
        try {
          const cleanedUrl = cleanImageUrl(buildingImg.src);
          const proxyUrl = `/api/proxy-image?url=${encodeURIComponent(cleanedUrl)}`;
          buildingBase64 = await loadImageThroughProxy(proxyUrl);
        } catch {
          try {
            const cleanedUrl = cleanImageUrl(buildingImg.src);
            buildingBase64 = await loadImageWithFetch(cleanedUrl);
          } catch {
            buildingBase64 = createImagePlaceholder('Image du bâtiment');
          }
        }
      }
    }
    
    // Charger les images de style depuis unitSummary-styleImagesGrid (thumbnails seulement)
    const styleImages: string[] = [];
    const styleImagesGrid = document.querySelector('.unitSummary-styleImagesGrid');
    
    if (styleImagesGrid) {
      // Chercher spécifiquement les thumbnails, éviter les images agrandies
      let styleImgs = styleImagesGrid.querySelectorAll('img.thumbnail, .thumbnail img') as NodeListOf<HTMLImageElement>;
      
      // Si pas de thumbnails spécifiques, prendre les images dans les éléments de grille
      if (styleImgs.length === 0) {
        styleImgs = styleImagesGrid.querySelectorAll('.grid-item img, .style-item img') as NodeListOf<HTMLImageElement>;
      }
      
      // Fallback: prendre toutes les images mais filtrer par taille (les thumbnails sont plus petits)
      if (styleImgs.length === 0) {
        const allImgs = styleImagesGrid.querySelectorAll('img') as NodeListOf<HTMLImageElement>;
        styleImgs = Array.from(allImgs).filter(img => 
          img.offsetWidth <= 300 && img.offsetHeight <= 300 // Filtrer par taille de thumbnail
        ) as unknown as NodeListOf<HTMLImageElement>;
      }
      
      const processedUrls = new Set<string>(); // Pour éviter les doublons
      
      for (const styleImg of styleImgs) {
        const originalUrl = cleanImageUrl(styleImg.src);
        
        // Éviter les doublons
        if (processedUrls.has(originalUrl)) {
          continue;
        }
        processedUrls.add(originalUrl);
        
        try {
          const proxyUrl = `/api/proxy-image?url=${encodeURIComponent(originalUrl)}`;
          const styleImageBase64 = await loadImageThroughProxy(proxyUrl);
          styleImages.push(styleImageBase64);
        } catch {
          try {
            const styleImageBase64 = await loadImageWithFetch(originalUrl);
            styleImages.push(styleImageBase64);
          } catch {
            // Ignorer les images qui ne peuvent pas être chargées
          }
        }
      }
    }
    
    return { unitPlanBase64, buildingBase64, styleImages };
  }

  async function downloadPdf(options: PdfGeneratorOptions): Promise<void> {
    try {
      isCreatingPdf.value = true;
      
      const delaySeconds = pdfCreationDelay.value / 1000;
      pdfPreparationStatus.value = t('pdf.preparing');

      for (let i = delaySeconds; i > 0; i--) {
        pdfPreparationStatus.value = t('pdf.preparing');
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
      pdfPreparationStatus.value = t('pdf.generating');
      
      try {
        await document.fonts.load('16px Inter');
      } catch {
        // Erreur lors du chargement de la police
      }
      
      const logoBase64 = await loadLogoAsBase64('/images/logo-gesteco.png');
      const slopeFooterBase64 = await loadLogoAsBase64('/images/slope-footer-unit-pdf.png');
      const { unitPlanBase64, buildingBase64, styleImages } = await loadUnitImages();
      
      const htmlContent = generatePdfHtmlContent(options, logoBase64, unitPlanBase64, buildingBase64, styleImages);

      // Extraire le titre du contenu HTML généré
      const titleElement = document.querySelector('.title-container');
      const pageTitle = titleElement ? titleElement.textContent?.trim() || 'Fiche Unité' : 'Fiche Unité';
      
      // Nettoyer le titre pour le nom de fichier (enlever caractères spéciaux)
      const cleanTitle = pageTitle
        .normalize('NFD') // Décomposer les caractères accentués
        .replace(/[\u0300-\u036f]/g, '') // Enlever les diacritiques (accents)
        .replace(/[^\w\s-]/g, '') // Enlever caractères spéciaux restants
        .replace(/\s+/g, '-') // Remplacer espaces par des tirets
        .toLowerCase();

      const opt = {
        margin: [0, 0, 0, 0] as [number, number, number, number],
        filename: `${cleanTitle}.pdf`,
        image: {
          type: 'jpeg' as const,
          quality: 0.9
        },
        enableLinks: false,
        pagebreak: { mode: ['avoid-all', 'css', 'legacy'] },
        html2canvas: {
          scale: 2,
          useCORS: false,
          allowTaint: true,
          logging: false,
          letterRendering: true,
          imageTimeout: 15000,
          onclone: function(clonedDoc: Document) {
            const link = clonedDoc.createElement('link');
            link.rel = 'stylesheet';
            link.href = 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap';
            clonedDoc.head.appendChild(link);
            
            return new Promise((resolve) => {
              setTimeout(() => resolve(clonedDoc), 500);
            });
          }
        },
        jsPDF: {
          unit: 'in',
          format: 'a4',
          orientation: 'portrait' as const,
          compressPDF: true
        }
      };

      html2pdf().set(opt).from(htmlContent).toPdf().get('pdf').then((pdf: any) => {
        const totalPages = pdf.internal.getNumberOfPages();
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        
        // Ajouter le footer uniquement sur la dernière page
        pdf.setPage(totalPages);
        
        // Hauteur totale du footer
        const footerHeight = 1.75;
        const footerStartY = pageHeight - footerHeight;
        
        // Ajouter l'image de décoration du footer (slope) - en haut du footer
        if (slopeFooterBase64) {
          const slopeHeight = 0.45;
          pdf.addImage(slopeFooterBase64, 'PNG', 0, footerStartY - slopeHeight, pageWidth, slopeHeight);
        }
        
        // Fond du footer (gris foncé #2c2c2c)
        pdf.setFillColor(44, 44, 44);
        pdf.rect(0, footerStartY, pageWidth, footerHeight, 'F');
        
        // Configuration du texte
        pdf.setTextColor(255, 255, 255);
        
        // Section principale du footer avec texte et logo
        const mainSectionY = footerStartY + 0.50;
        
        // Texte principal du footer (gauche)
        pdf.setFontSize(10);
        const footerText = options.t('pdf.footer.text');
        const maxTextWidth = 5.15;
        const textLines = pdf.splitTextToSize(footerText, maxTextWidth);
        pdf.text(textLines, 0.20, mainSectionY);
        
        // Logo Gesteco (droite)
        if (logoBase64) {
          const logoHeight = 0.25;
          const logoWidth = 2.25;
          pdf.addImage(logoBase64, 'PNG', pageWidth - logoWidth - 0.2, mainSectionY - 0.05, logoWidth, logoHeight);
        }
        
        // Section inférieure avec site web et téléphone
        const bottomSectionY = footerStartY + 1.20;
        const emailSectionY = bottomSectionY + 0.30;
        
        // Site web (gauche)
        pdf.setFontSize(14);
        pdf.text(options.t('pdf.footer.website'), 0.20, bottomSectionY);
        
        // Téléphone (droite)
        pdf.setFontSize(14);
        const phoneText = options.t('pdf.footer.phone');
        pdf.text(phoneText, pageWidth - 0.20, bottomSectionY, { align: 'right' });
        
        // Adresse courriel (droite, sous le téléphone)
        pdf.setFontSize(12);
        const emailAddress = options.isRentalMode ? 'location@gesteco.ca' : 'ventes@gesteco.ca';
        pdf.text(emailAddress, pageWidth - 0.20, emailSectionY, { align: 'right' });
        
        pdf.save(`${cleanTitle}.pdf`);
      });
      
    } catch {
      alert('Erreur lors de la generation du PDF');
    } finally {
      isCreatingPdf.value = false;
      pdfPreparationStatus.value = '';
    }
  }

  async function previewHtml(options: PdfGeneratorOptions): Promise<void> {
    try {
      const logoBase64 = await loadLogoAsBase64('/images/logo-gesteco.png');
      const { unitPlanBase64, buildingBase64, styleImages } = await loadUnitImages();

      const htmlContent = generatePdfHtmlContent(options, logoBase64, unitPlanBase64, buildingBase64, styleImages);

      const previewWindow = window.open('', '_blank');
      if (previewWindow) {
        previewWindow.document.write(`
          <!DOCTYPE html>
          <html>
          <head>
            <title>Aperçu PDF - ${options.quoteInfo?.unit?.unitNumber || 'Unité'}</title>
            <link rel="preconnect" href="https://fonts.googleapis.com">
            <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
            <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap" rel="stylesheet">
            <style>
              body { margin: 0; padding: 20px; background-color: #f5f5f5; }
              .pdf-preview { 
                max-width: 800px; 
                margin: 0 auto; 
                background: #2c2c2c; 
                box-shadow: 0 4px 6px rgba(0,0,0,0.1);
                border-radius: 8px;
              }
            </style>
          </head>
          <body>
            <div class="pdf-preview">
              ${htmlContent}
            </div>
          </body>
          </html>
        `);
        previewWindow.document.close();
      }
    } catch (error) {
      alert('Erreur lors de la génération de l\'aperçu');
    }
  }

  return {
    isCreatingPdf,
    pdfCreationDelay,
    pdfPreparationStatus,
    downloadPdf,
    previewHtml
  };
}
